/**
 * Error Logger - logs errors to console and optionally to server
 */

import { AppError, ErrorLog, ErrorType } from './types';

type LogLevel = 'debug' | 'info' | 'warning' | 'error';

interface LogEntry {
  id: string;
  timestamp: Date;
  level: LogLevel;
  message: string;
  error?: AppError;
  context?: Record<string, unknown>;
}

class ErrorLogger {
  private logs: LogEntry[] = [];
  private maxLogs = 500;
  private listeners: ((entry: LogEntry) => void)[] = [];
  private apiEndpoint?: string;

  configure(options: { apiEndpoint?: string }) {
    this.apiEndpoint = options.apiEndpoint;
  }

  debug(message: string, context?: Record<string, unknown>): void {
    this.log('debug', message, undefined, context);
  }

  info(message: string, context?: Record<string, unknown>): void {
    this.log('info', message, undefined, context);
  }

  warn(message: string, context?: Record<string, unknown>): void {
    this.log('warning', message, undefined, context);
  }

  error(error: AppError | string, context?: Record<string, unknown>): void {
    if (typeof error === 'string') {
      this.log('error', error, undefined, context);
    } else {
      this.log('error', error.message, error, context);
    }
  }

  private log(
    level: LogLevel,
    message: string,
    error?: AppError,
    context?: Record<string, unknown>
  ): void {
    const entry: LogEntry = {
      id: this.generateId(),
      timestamp: new Date(),
      level,
      message,
      error,
      context,
    };

    this.logs.push(entry);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    // Notify listeners
    this.listeners.forEach((listener) => listener(entry));

    // Log to console
    this.logToConsole(entry);

    // Send to server if endpoint configured
    if (this.apiEndpoint && level === 'error') {
      this.sendToServer(entry);
    }
  }

  private logToConsole(entry: LogEntry): void {
    const timestamp = entry.timestamp.toISOString();
    const prefix = `[${entry.level.toUpperCase()}] ${timestamp}`;

    switch (entry.level) {
      case 'debug':
        console.debug(prefix, entry.message, entry.context);
        break;
      case 'info':
        console.info(prefix, entry.message, entry.context);
        break;
      case 'warning':
        console.warn(prefix, entry.message, entry.context);
        break;
      case 'error':
        console.error(
          prefix,
          entry.message,
          entry.error,
          entry.context
        );
        break;
    }
  }

  private async sendToServer(entry: LogEntry): Promise<void> {
    if (!this.apiEndpoint) return;

    try {
      await fetch(this.apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          level: entry.level,
          message: entry.message,
          error: entry.error,
          context: entry.context,
          timestamp: entry.timestamp.toISOString(),
        }),
      });
    } catch {
      // Silently fail - don't break app for logging
    }
  }

  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  getLogs(options?: { level?: LogLevel; limit?: number }): LogEntry[] {
    let filtered = this.logs;

    if (options?.level) {
      filtered = filtered.filter((l) => l.level === options.level);
    }

    if (options?.limit) {
      filtered = filtered.slice(-options.limit);
    }

    return filtered;
  }

  getErrorLogs(limit = 50): LogEntry[] {
    return this.logs
      .filter((l) => l.level === 'error')
      .slice(-limit);
  }

  getErrorsByType(type: ErrorType): LogEntry[] {
    return this.logs.filter(
      (l) => l.error?.type === type
    );
  }

  getStatistics(): Record<ErrorType, number> {
    const stats: Record<ErrorType, number> = {} as Record<ErrorType, number>;

    this.logs
      .filter((l) => l.error?.type)
      .forEach((l) => {
        const type = l.error!.type;
        stats[type] = (stats[type] || 0) + 1;
      });

    return stats;
  }

  clear(): void {
    this.logs = [];
  }

  subscribe(listener: (entry: LogEntry) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  async export(): Promise<string> {
    return JSON.stringify(this.logs, null, 2);
  }
}

export const errorLogger = new ErrorLogger();
export default errorLogger;
